/**
 * DriverSiteInfoLink — small toolbar shown above the booking form for any
 * saved booking. Lets staff copy / open / email the integrated driver
 * site-info link (per-booking, includes site map + ALL rules).
 *
 * Link format: <origin>/d/{driver_site_info_token}
 *
 * Token is generated automatically on the DB side (migration 048). If the
 * booking row doesn't yet have one (legacy row), we fall back to refusing
 * to render — admins can re-run the backfill.
 */
import { Button } from "@/components/ui/button";
import { Mail, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function DriverSiteInfoLink({ booking }) {
  const token = booking?.driver_site_info_token;
  if (!token) return null;

  const url = `${window.location.origin}/d/${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Driver site-info link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const mail = () => {
    const subject = `Site map & rules — booking ${booking.reference_number || ""}`;
    const body    = `Hi,\n\nHere is the site map and driver instructions for your booking ${booking.reference_number || ""}:\n\n${url}\n\nPlease review before arrival.\n`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border bg-blue-50/40 px-3 py-2 text-xs"
      data-testid="driver-site-info-toolbar"
    >
      <span className="font-medium text-blue-900">Driver site-info link</span>
      <code className="text-[11px] font-mono text-slate-600 bg-white border rounded px-1.5 py-0.5 truncate max-w-[260px]" title={url}>
        {url}
      </code>
      <div className="ml-auto flex items-center gap-1.5">
        <Button size="sm" variant="ghost" onClick={copy} data-testid="driver-link-copy">
          <Link2 className="h-3.5 w-3.5 mr-1" /> Copy
        </Button>
        <Button size="sm" variant="ghost" onClick={mail} data-testid="driver-link-email">
          <Mail className="h-3.5 w-3.5 mr-1" /> Email
        </Button>
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid="driver-link-open">
          <Button size="sm" variant="outline">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
          </Button>
        </a>
      </div>
    </div>
  );
}
